import React, { useState, useEffect } from 'react';
import { 
    collection, 
    onSnapshot, 
    doc, 
    writeBatch, 
    getDoc 
} from 'firebase/firestore';
import { db, appId } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { generateTimeSlots } from '../utils/helpers';
import MessageBox from '../components/MessageBox';
import { ADMIN_WHATSAPP_WEBHOOK_URL_1, ADMIN_WHATSAPP_WEBHOOK_URL_2 } from '../utils/constants';

function BookingPage({ setPendingBookingsCount, setUserBookedSpotsCount, setUserPendingBookingsCount, blockedDays, bookingsDisabled, bookings }) {
    const { currentUser, userDetails } = useAuth();
    const [currentWeekStart, setCurrentWeekStart] = useState(() => {
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Adjust for Sunday
        const monday = new Date(today.setDate(diff));
        monday.setHours(0, 0, 0, 0);
        return monday;
    });
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        today.setHours(0,0,0,0);
        return today;
    });
    const [selectedSlots, setSelectedSlots] = useState([]);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [loadingBookings, setLoadingBookings] = useState(false);
    const [bookingToCancel, setBookingToCancel] = useState(null);
    const [bookingToConfirm, setBookingToConfirm] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUserForBooking, setSelectedUserForBooking] = useState(null);
    const [loadingAdminData, setLoadingAdminData] = useState(true);

    const userId = currentUser?.uid;
    const isAdmin = userDetails?.isAdmin;

    // --- MOVED HELPER FUNCTIONS UP ---
    const getWeekDays = (startOfWeek) => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            days.push(day);
        }
        return days;
    };

    const weekDays = getWeekDays(currentWeekStart);

    // --- HOOKS MUST BE CALLED HERE (BEFORE ANY RETURN) ---

    useEffect(() => {
        if (!isAdmin || !db || !userDetails) {
            setLoadingAdminData(false);
            return;
        };

        setLoadingAdminData(true);
        const usersColRef = collection(db, 'artifacts', appId, 'users');
        const unsubscribe = onSnapshot(usersColRef, (snapshot) => {
            const fetchedUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Filter out deactivated users for admin booking purposes
            const activeUsers = fetchedUsers.filter(u => u.isActive !== false); 
            setAllUsers(activeUsers);
            if (!selectedUserForBooking) {
                setSelectedUserForBooking(activeUsers.find(u => u.id === userId));
            }
            setLoadingAdminData(false);
        }, (error) => {
            console.error("Error fetching users for admin:", error);
            setMessage(`Could not load user list for admin view. Error: ${error.message}`);
            setMessageType("error");
            setLoadingAdminData(false);
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, userId, userDetails, selectedUserForBooking]); 

    useEffect(() => {
        if (!userId || !bookings) return;
        
        const currentUserBookings = bookings.filter(b => b.userId === userId);
        const userApprovedCount = currentUserBookings.filter(b => b.status === 'approved').length;
        const userPendingCount = currentUserBookings.filter(b => b.status === 'pending').length;
        if (setPendingBookingsCount) setPendingBookingsCount(bookings.filter(b => b.status === 'pending').length);
        if (setUserBookedSpotsCount) setUserBookedSpotsCount(userApprovedCount);
        if (setUserPendingBookingsCount) setUserPendingBookingsCount(userPendingCount);
    }, [bookings, userId, setPendingBookingsCount, setUserBookedSpotsCount, setUserPendingBookingsCount]);

    // --- LOGIC FUNCTIONS ---

    const allTimeSlots = generateTimeSlots(selectedDate);

    const getSlotStatus = (slotTime) => {
        const now = new Date();
        
        if (slotTime.getTime() < now.getTime()) {
            return { status: 'past' };
        }
        
        if (!isAdmin) {
            // 14 days from now
            const maxBookableDate = new Date();
            maxBookableDate.setDate(maxBookableDate.getDate() + 14);
            
            if (slotTime > maxBookableDate) {
                return { status: 'blocked', reason: 'Booking not yet open' };
            }
        }

        const blockedSlot = blockedDays.find(bs => 
            bs.startTime && bs.startTime.getTime() === slotTime.getTime()
        );
        if (blockedSlot) {
            return { status: 'blocked', reason: blockedSlot.reason };
        }

        const overlappingBooking = bookings.find(booking =>
            booking.startTime.getTime() === slotTime.getTime() &&
            booking.status !== 'rejected' &&
            booking.status !== 'Cancelled by Admin' &&
            booking.status !== 'Cancelled by Admin (Blocked)' 
        );

        if (overlappingBooking) {
            return { status: overlappingBooking.status, booking: overlappingBooking };
        }

        const isSelectedByUserForBooking = selectedSlots.some(s => s.getTime() === slotTime.getTime());
        if (isSelectedByUserForBooking) {
            return { status: 'selected' };
        }

        return { status: 'available' };
    };

    const handleSlotClick = (slotTime) => {
        const statusInfo = getSlotStatus(slotTime);
        
        if (['past', 'approved', 'pending', 'blocked'].includes(statusInfo.status)) {
            setMessage(`This slot is ${statusInfo.status}.`);
            setMessageType('error');
            return;
        }
        
        const userToCheck = isAdmin ? selectedUserForBooking : userDetails;

        if (userToCheck) {
            const availableSpots = userToCheck.availableSpots || 0;
            if (selectedSlots.length >= availableSpots && !selectedSlots.some(s => s.getTime() === slotTime.getTime())) {
                setMessage(`The selected user (or you) does not have enough spots available. They currently have ${availableSpots} spot(s) available.`);
                setMessageType('error');
                return;
            }
        }

        if (selectedSlots.some(s => s.getTime() === slotTime.getTime())) {
            setSelectedSlots(selectedSlots.filter(s => s.getTime() !== slotTime.getTime()));
        } else {
            setSelectedSlots([...selectedSlots, slotTime].sort((a, b) => a.getTime() - b.getTime()));
        }
    };

    const handleRequestBookingClick = () => {
        if (selectedSlots.length === 0) {
            setMessage("Please select at least one spot to book.");
            setMessageType('error');
            return;
        }

        const userToCheck = isAdmin ? selectedUserForBooking : userDetails;
        if (!userToCheck || userToCheck.availableSpots < selectedSlots.length) {
            setMessage(`The user only has ${userToCheck?.availableSpots || 0} spot(s) available. Please select fewer spots.`);
            setMessageType('error');
            return;
        }

        setBookingToConfirm(true);
    };

    const handleConfirmBooking = async () => {
        setBookingToConfirm(null);
        const bookingUser = isAdmin ? selectedUserForBooking : userDetails;
        
        if (!bookingUser || !bookingUser.uid) {
            setMessage("Error: User data is not available. Please select a user or try logging in again.");
            setMessageType('error');
            return;
        }
        
        if (!db) {
            setMessage("Firebase database is not initialized.");
            setMessageType('error');
            return;
        }

        setMessage('');
        setLoadingBookings(true);
        try {
            const batch = writeBatch(db);
            const bookingsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'bookings');
            
            const userDocRef = doc(db, 'artifacts', appId, 'users', bookingUser.uid);
            const newAvailableSpots = (bookingUser.availableSpots || 0) - selectedSlots.length;
            
            if (newAvailableSpots < 0) {
                setMessage("The user does not have enough spots available to complete this booking.");
                setMessageType('error');
                setLoadingBookings(false);
                return;
            }

            batch.update(userDocRef, { availableSpots: newAvailableSpots });

            const bookingDetailsForNotify = [];
            for (const slotTime of selectedSlots) {
                const currentStatus = getSlotStatus(slotTime);
                if (currentStatus.status !== 'available' && currentStatus.status !== 'selected') {
                    setMessage(`One or more selected slots became unavailable: ${slotTime.toLocaleTimeString()}. Please review your selection.`);
                    setMessageType('error');
                    setSelectedSlots([]);
                    setLoadingBookings(false);
                    return;
                }

                const endTime = new Date(slotTime);
                endTime.setMinutes(endTime.getMinutes() + 30);
                
                const bookingStatus = 'pending';

                const newBookingDoc = doc(bookingsColRef);
                batch.set(newBookingDoc, {
                    userId: bookingUser.uid,
                    userName: bookingUser.displayName || bookingUser.email, 
                    userColor: bookingUser.color,
                    startTime: slotTime,
                    endTime: endTime,
                    status: bookingStatus,
                    createdAt: new Date()
                });
                bookingDetailsForNotify.push(`${slotTime.toLocaleDateString()} ${slotTime.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`);
            }
            
            await batch.commit();
            
            if (ADMIN_WHATSAPP_WEBHOOK_URL_1 || ADMIN_WHATSAPP_WEBHOOK_URL_2) {
                try {
                    const message = `🐕 *New Booking Alert! 🐕* \nUser: ${bookingUser.displayName}\nSpots: ${selectedSlots.length}\nSlots: ${bookingDetailsForNotify.join(', ')}`;
                    
                    const sendNotification = async (webhookUrl) => {
                        if (!webhookUrl) return;
                        const separator = webhookUrl.includes('?') ? '&' : '?';
                        const fetchUrl = `${webhookUrl}${separator}text=${encodeURIComponent(message)}`;
                        await fetch(fetchUrl, { mode: 'no-cors' });
                    };

                    await sendNotification(ADMIN_WHATSAPP_WEBHOOK_URL_1);
                    await sendNotification(ADMIN_WHATSAPP_WEBHOOK_URL_2); 

                    console.log("WhatsApp notification signals sent.");
                } catch (notifyErr) {
                    console.warn("Failed to trigger WhatsApp notification:", notifyErr);
                }
            }

            setMessage(`Successfully submitted ${selectedSlots.length} booking(s) for admin approval!`);
            setMessageType('info');
            setSelectedSlots([]);
        } catch (error) {
            console.error("Error booking spots:", error);
            setMessage(`Error booking spots: ${error.message}`);
            setMessageType('error');
        } finally {
            setLoadingBookings(false);
        }
    };

    const handleCancelBookingConfirm = () => {
        setBookingToConfirm(null);
    };

    const handleWeekChange = (weeks) => {
        const newWeekStart = new Date(currentWeekStart);
        newWeekStart.setDate(newWeekStart.getDate() + (weeks * 7));
        setCurrentWeekStart(newWeekStart);
        
        const today = new Date();
        today.setHours(0,0,0,0);
        if (newWeekStart.getTime() <= today.getTime() && new Date(newWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).getTime() >= today.getTime()) {
            setSelectedDate(today);
        } else {
            setSelectedDate(newWeekStart);
        }
        setSelectedSlots([]);
    };

    const handleDaySelect = (day) => {
        setSelectedDate(day);
        setSelectedSlots([]);
    };
    
    const handleAdminCancelClick = (booking) => {
        setBookingToCancel(booking);
    };

    const handleConfirmAdminCancel = async () => {
        if (!bookingToCancel || !isAdmin) return;
        try {
            const bookingDocRef = doc(db, 'artifacts', appId, 'public', 'data', 'bookings', bookingToCancel.id);
            
            const batch = writeBatch(db);
            
            const userDocRef = doc(db, 'artifacts', appId, 'users', bookingToCancel.userId);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
                const currentSpots = userDoc.data().availableSpots || 0;
                const newSpots = currentSpots + 1;
                batch.update(userDocRef, { availableSpots: newSpots });
            }
            
            batch.update(bookingDocRef, { status: 'Cancelled by Admin' });
            
            await batch.commit();

            setMessage('Booking successfully cancelled and spot refunded.');
            setMessageType('info');
        } catch (error) {
            setMessage(`Error cancelling booking: ${error.message}`);
            setMessageType('error');
        } finally {
            setBookingToCancel(null);
        }
    };

    // --- RENDER LOGIC ---

    // 1. Check if loading
    if (loadingBookings) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-80px)] bg-gray-50">
                <div className="text-xl font-semibold text-gray-700">Loading bookings...</div>
            </div>
        );
    }

    // 2. Check if disabled (moved from top to here to respect Hook rules)
    if (bookingsDisabled && !isAdmin) {
        return (
            <div className="flex items-center justify-center min-h-[calc(100vh-80px)] p-6">
                <div className="p-8 bg-white rounded-lg shadow-lg text-center border-t-4 border-red-500 max-w-lg mx-auto">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Bookings Temporarily Disabled</h2>
                    <p className="text-lg text-gray-700">
                        We're sorry, but the admin has temporarily disabled bookings. Please check back later.
                    </p>
                    <p className="mt-4 text-sm text-gray-500">
                        Thank you for your understanding.
                    </p>
                </div>
            </div>
        );
    }

    // 3. Main Render
    const WEEKS_IN_ADVANCE_LIMIT = 2; 
    const maxAllowedDate = new Date();
    maxAllowedDate.setDate(maxAllowedDate.getDate() + (WEEKS_IN_ADVANCE_LIMIT * 7));
    maxAllowedDate.setHours(23, 59, 59, 999); 

    const nextWeekStart = new Date(currentWeekStart);
    nextWeekStart.setDate(nextWeekStart.getDate() + 7);
    nextWeekStart.setHours(0, 0, 0, 0);

    const isNextDisabled = !isAdmin && nextWeekStart.getTime() > maxAllowedDate.getTime();

    return (
        <div className="container mx-auto p-6 bg-white rounded-lg shadow-lg my-6">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />
            {bookingToCancel && (
                <MessageBox
                    message={`Are you sure you want to cancel the booking for ${bookingToCancel.userName} at ${bookingToCancel.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}? This will also refund the spot to the user.`}
                    onClose={() => setBookingToCancel(null)}
                >
                    <div className="mt-4 flex justify-center space-x-4">
                        <button onClick={handleConfirmAdminCancel} className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Confirm Cancel</button>
                        <button onClick={() => setBookingToCancel(null)} className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">Back</button>
                    </div>
                </MessageBox>
            )}
            {bookingToConfirm && (
                <MessageBox
                    message={`You are about to submit ${selectedSlots.length} booking(s). Your spot count will be reduced immediately upon submission. Are you sure you want to continue?`}
                    onClose={handleCancelBookingConfirm}
                >
                    <div className="mt-4 flex justify-center space-x-4">
                        <button onClick={handleConfirmBooking} className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">Confirm Booking</button>
                        <button onClick={handleCancelBookingConfirm} className="px-6 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">Cancel</button>
                    </div>
                </MessageBox>
            )}

            <h2 className="font-display text-5xl text-gray-800 mb-6 text-center tracking-wider">
                Please Book Your Spot
            </h2>

            {isAdmin && (
                <div className="mt-6 mb-8 p-6 border border-gray-200 rounded-lg shadow-sm bg-blue-50">
                    <h3 className="text-2xl font-bold text-blue-800 mb-4 text-center">Admin: Book for User</h3>
                    {loadingAdminData ? <p className="text-center">Loading user list...</p> : (
                        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                            <label htmlFor="userSelect" className="text-lg font-medium text-gray-700">Select User:</label>
                            <select
                                id="userSelect"
                                value={selectedUserForBooking?.id || ''}
                                onChange={(e) => setSelectedUserForBooking(allUsers.find(u => u.id === e.target.value))}
                                className="p-2 border border-gray-300 rounded-md text-lg focus:ring-blue-500 focus:border-blue-500 bg-white"
                            >
                                <option value="">Select a user...</option>
                                {allUsers.filter(u => u.isActive !== false).map(user => (
                                    <option key={user.id} value={user.id}>{user.displayName || user.email}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
            )}
            
            {userDetails && (
                <div className="text-center mb-6 p-4 bg-gray-100 rounded-lg shadow-inner">
                    <p className="text-xl font-bold text-gray-700">
                        {isAdmin && selectedUserForBooking ? 
                            <span>User <span style={{ color: selectedUserForBooking.color }}>{selectedUserForBooking.displayName}</span> has </span> :
                            <span>You have </span>
                        }
                        <span className="text-blue-600">{(isAdmin ? selectedUserForBooking?.availableSpots : userDetails.availableSpots) || 0}</span> spots available.
                    </p>
                </div>
            )}

            <div className="flex justify-center items-center space-x-4 mb-4">
                <button onClick={() => handleWeekChange(-1)} className="p-3 rounded-full bg-blue-500 text-white shadow-md hover:bg-blue-600 transition duration-200">&lt; Prev</button>
                <span className="text-xl font-semibold text-gray-700 text-center">
                    {currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <button 
                    onClick={() => handleWeekChange(1)} 
                    disabled={isNextDisabled}
                    className={`p-3 rounded-full text-white shadow-md transition duration-200 ${
                        isNextDisabled 
                            ? 'bg-gray-300 cursor-not-allowed' 
                            : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                >
                    Next &gt;
                </button>
            </div>

            <div className="flex justify-center space-x-1 mb-6 sm:space-x-2">
                {weekDays.map(day => (
                    <button
                        key={day.toISOString()}
                        onClick={() => handleDaySelect(day)}
                        className={`flex flex-col items-center p-1 rounded-md text-xs font-semibold transition duration-200 sm:px-4 sm:py-2 sm:text-sm sm:rounded-full ${selectedDate.toDateString() === day.toDateString() ? 'bg-emerald-500 text-white shadow-md' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                    >
                        <span className="block sm:hidden">
                            {day.toLocaleDateString('en-US', { weekday: 'narrow' })}
                        </span>
                        <span className="hidden sm:block">
                            {day.toLocaleDateString('en-US', { weekday: 'short' })}
                        </span>
                        <span className="block font-bold text-sm">
                            {day.getDate()}
                        </span>
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
                {allTimeSlots.map((slotTime, index) => {
                    const statusInfo = getSlotStatus(slotTime);
                    const isPast = statusInfo.status === 'past';
                    const isBooked = statusInfo.status === 'approved';
                    const isSelected = statusInfo.status === 'selected';
                    const isPending = statusInfo.status === 'pending';
                    const isBlocked = statusInfo.status === 'blocked';

                    let slotClasses = "p-3 rounded-lg text-center font-medium transition duration-200 cursor-pointer border-2 flex justify-center items-center text-lg relative h-20";
                    let slotStyle = {};
                    let title = '';

                    if (isPast) {
                        slotClasses += " bg-gray-200 text-gray-500 line-through cursor-not-allowed border-gray-300";
                    } else if (isBlocked) {
                        slotClasses += " bg-red-100 text-red-800 cursor-not-allowed border-red-200 flex-col space-y-1";
                        title = slotTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' - ' + statusInfo.reason;
                    } else if (isBooked) {
                        slotClasses += " text-white cursor-pointer border-transparent";
                        slotStyle = { backgroundColor: statusInfo.booking.userColor };
                    } else if (isPending) {
                        slotClasses += " bg-yellow-100 text-yellow-800 border-yellow-300 cursor-not-allowed";
                    } else if (isSelected) {
                        slotClasses += " bg-green-500 text-white border-green-600 shadow-md";
                    } else {
                        slotClasses += " bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200";
                    }

                    return (
                        <div
                            key={index}
                            className={slotClasses}
                            style={slotStyle}
                            onClick={() => handleSlotClick(slotTime)}
                            title={title}
                        >
                            {isBlocked ? (
                                <>
                                    <span className="flex items-center justify-center text-xl text-red-500">
                                    </span>
                                    <span className="text-sm font-bold text-red-800 leading-tight">UNAVAILABLE</span>
                                    <span className="block text-xs text-red-600 opacity-80 leading-tight truncate px-1" title={statusInfo.reason}>
                                        ({statusInfo.reason})
                                    </span>
                                </>
                            ) : (
                                <>
                                    {slotTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    {isBooked && <span className="block text-xs opacity-80 ml-2">({statusInfo.booking.userName.split(' ')[0]})</span>}
                                    {isPending && <span className="block text-xs opacity-80 ml-2">Pending</span>}
                                </>
                            )}
                            {isBooked && isAdmin && (
                                <button 
                                    onClick={(e) => { e.stopPropagation(); handleAdminCancelClick(statusInfo.booking); }} 
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center text-xs font-bold"
                                    title="Cancel Booking and Refund Spot"
                                >&times;</button>
                            )}
                        </div>
                    );
                })}
            </div>

            {selectedSlots.length > 0 && (
                <div className="text-center mb-6">
                    <p className="text-lg font-semibold text-gray-700 mb-3">Selected Slots:</p>
                    <div className="flex flex-wrap justify-center gap-2 mb-4">
                        {selectedSlots.map((slot, index) => (
                            <span key={index} className="px-4 py-2 bg-blue-500 text-white rounded-full text-sm shadow-sm">
                                {slot.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        ))}
                    </div>
                    <button
                        onClick={handleRequestBookingClick}
                        className="px-8 py-4 bg-green-600 text-white text-lg font-bold rounded-full shadow-xl hover:bg-green-700 focus:outline-none focus:ring-4 focus:ring-green-500 focus:ring-opacity-50 transition duration-300 ease-in-out transform hover:scale-105"
                    >
                        Request Booking ({selectedSlots.length} spots)
                    </button>
                </div>
            )}
        </div>
    );
}

export default BookingPage;